package org.vreo.devicehub.features.device.presentation.registerDevice

import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

import org.vreo.devicehub.features.core.domain.device.device.ProductType
import org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu.LargeDropdownMenu
import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.device_details_serial_number_label
import devicehub.composeapp.generated.resources.device_details_type_label
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.register_device_access_settings_label
import devicehub.composeapp.generated.resources.register_device_auto_serial_toggle_title
import devicehub.composeapp.generated.resources.register_device_button_title
import devicehub.composeapp.generated.resources.register_device_connect_button_title
import devicehub.composeapp.generated.resources.register_device_connected_note
import devicehub.composeapp.generated.resources.register_device_not_connected_note
import devicehub.composeapp.generated.resources.register_device_title
import org.vreo.devicehub.features.device.presentation.components.DeviceDetails
import org.vreo.devicehub.features.device.presentation.components.toUiText

/**
 * Register device screen root
 *
 * @param viewModel view model
 * @param onRegisterDeviceSuccess callback to be called on successful register device operation
 * @param onBackNavigation callback to be called on back navigation
 */
@Composable
fun RegisterDeviceScreenRoot(
    viewModel: RegisterDeviceViewModel = koinViewModel(),
    onRegisterDeviceSuccess: () -> Unit,
    onBackNavigation: () -> Unit
) {
    val state: RegisterDeviceState by viewModel.state.collectAsStateWithLifecycle()

    RegisterDeviceScreenView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is RegisterDeviceActions.OnRegisterDeviceClicked -> {
                    action.copy(
                        callback = onRegisterDeviceSuccess
                    )
                }

                is RegisterDeviceActions.OnBackNavigation -> {
                    onBackNavigation()
                    action
                }

                else -> action
            }
            viewModel.onAction(actionRequest)
        }
    )
}

/**
 * Register device screen view
 *
 * @param state state to present
 * @param onAction action handler
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RegisterDeviceScreenView(
    state: RegisterDeviceState,
    onAction: (RegisterDeviceActions) -> Unit
) {
    val scrollState = rememberScrollState()
    val focusManager = LocalFocusManager.current

    Scaffold(
        topBar = {
            TopAppBar(
                title = {},
                navigationIcon = {
                    IconButton(
                        onClick = {
                            onAction(RegisterDeviceActions.OnBackNavigation())
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = null,
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            contentAlignment = Alignment.TopCenter,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .padding(8.dp)
                    .verticalScroll(scrollState)
            ) {
                Text(
                    text = stringResource(Res.string.register_device_title),
                    style = MaterialTheme.typography.displaySmall.copy(
                        fontWeight = FontWeight.Bold
                    )
                )

                HorizontalDivider(
                    modifier = Modifier
                        .padding(bottom = 8.dp)
                )

                Row(
                    verticalAlignment = Alignment.Top,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    // Main content and control section
                    Column(
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                        modifier = Modifier.widthIn(max = 700.dp)
                    ) {
                        // Product type selection
                        LargeDropdownMenu(
                            label = stringResource(Res.string.device_details_type_label),
                            items = ProductType.VALUES.map { item ->
                                item.toUiText().asString()
                            },
                            selectedIndex = state.selectedProductIndex,
                            onItemSelected = {
                                onAction(RegisterDeviceActions.OnUpdateSelectedProduct(it))
                            },
                        )

                        // Serial number selection
                        Column(
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(
                                    text = stringResource(Res.string.register_device_auto_serial_toggle_title),
                                )

                                Switch(
                                    checked = state.autoGenerateSerialNumber,
                                    onCheckedChange = {
                                        onAction(
                                            RegisterDeviceActions.OnUpdateAutoGenerateSerialNumber(
                                                it
                                            )
                                        )
                                    }
                                )
                            }
                            OutlinedTextField(
                                value = state.serialNumber,
                                onValueChange = {
                                    onAction(RegisterDeviceActions.OnUpdateSerialNumber(it))
                                },
                                label = { Text(stringResource(Res.string.device_details_serial_number_label)) },
                                enabled = !state.autoGenerateSerialNumber,
                                singleLine = true,
                                isError = state.invalidSerialNumber,
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Text,
                                    imeAction = ImeAction.Next
                                ),
                                keyboardActions = KeyboardActions(
                                    onNext = { focusManager.moveFocus(FocusDirection.Down) }
                                ),
                                modifier = Modifier.fillMaxWidth()
                            )
                        }

                        // Device connection section
                        Text(
                            text = if (state.deviceConnected) {
                                stringResource(Res.string.register_device_connected_note)
                            } else {
                                stringResource(Res.string.register_device_not_connected_note)
                            },
                            color = if (state.deviceConnected) {
                                Color.Green
                            } else {
                                MaterialTheme.colorScheme.error
                            },
                            style = MaterialTheme.typography.bodyMedium.copy(
                                textAlign = TextAlign.Center,
                                fontWeight = FontWeight.Bold
                            ),
                            modifier = Modifier.padding(16.dp)
                        )

                        OutlinedTextField(
                            value = state.deviceIpAddress,
                            enabled = !state.deviceConnected,
                            onValueChange = {
                                onAction(RegisterDeviceActions.OnUpdateDeviceIpAddress(it))
                            },
                            label = { Text(stringResource(Res.string.register_device_access_settings_label)) },
                            singleLine = true,
                            isError = state.invalidSerialNumber,
                            keyboardOptions = KeyboardOptions(
                                keyboardType = KeyboardType.Text,
                                imeAction = ImeAction.Next
                            ),
                            keyboardActions = KeyboardActions(
                                onNext = { focusManager.moveFocus(FocusDirection.Down) }
                            ),
                            modifier = Modifier.fillMaxWidth()
                        )

                        FilledTonalButton(
                            onClick = {
                                onAction(RegisterDeviceActions.OnConnectToDeviceClicked())
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(24.dp)
                        ) {
                            Text(stringResource(Res.string.register_device_connect_button_title))
                        }

                        Spacer(
                            modifier = Modifier.padding(16.dp)
                        )

                        // Register device button
                        Button(
                            onClick = {
                                onAction(RegisterDeviceActions.OnRegisterDeviceClicked())
                            },
                            enabled = state.deviceConnected,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(24.dp)
                        ) {
                            Text(stringResource(Res.string.register_device_button_title))
                        }

                        Spacer(
                            modifier = Modifier.padding(32.dp)
                        )
                    }

                    // Device details
                    if (state.device != null) {
                        DeviceDetails(
                            device = state.device,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                        )
                    }
                }
            }

            // Conditional section for displaying loading and error states
            if (state.isLoading) {
                LoadingDialog()
            } else if (state.error != null) {
                ErrorDialog(
                    dialogTitle = stringResource(Res.string.error_general_title),
                    dialogText = state.error.asString(),
                    onDismissRequest = {
                        onAction(RegisterDeviceActions.OnClearError())
                    },
                    onConfirmation = {
                        onAction(RegisterDeviceActions.OnClearError())
                    }
                )
            }
        }
    }
}


@Preview
@Composable
fun RegisterDeviceScreenViewPreview() {
    MaterialTheme {
        RegisterDeviceScreenView(
            state = RegisterDeviceState(
                autoGenerateSerialNumber = true
            ),
            onAction = {},
        )
    }
}
